import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Header from "../../../components/ui/Header";
import Sidebar from "../../../components/ui/Sidebar";
import Breadcrumb from "../../../components/ui/Breadcrumb";
import Button from "../../../components/ui/Button";
import Icon from "../../../components/AppIcon";
import axios from "axios";
import API_BASE_URL from "../../../services/api";
import { toast } from "react-hot-toast";

const getAttendanceBadge = (status) => {
  switch ((status || "").toLowerCase()) {
    case "present":
    case "checked_in":
    case "checked-in":
    case "checkedin":
      return "bg-green-50 text-green-700 border border-green-200";
    case "absent":
      return "bg-red-50 text-red-700 border border-red-200";
    case "half_day":
    case "half-day":
    case "halfday":
      return "bg-yellow-50 text-yellow-700 border border-yellow-200";
    case "leave":
      return "bg-purple-50 text-purple-700 border border-purple-200";
    default:
      return "bg-gray-50 text-gray-700 border border-gray-200";
  }
};

const VendorDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const vendorIdNum = Number(id);

  const [vendor, setVendor] = useState(location.state?.vendor || null);
  const [loading, setLoading] = useState(!vendor);
  const [activeTab, setActiveTab] = useState("details");

  const [walletAmount, setWalletAmount] = useState("");
  const [walletDescription, setWalletDescription] = useState("");
  const [walletType, setWalletType] = useState("add");
  const [showWalletModal, setShowWalletModal] = useState(false);

  const [jobHistory, setJobHistory] = useState([]);
  const [walletHistory, setWalletHistory] = useState([]);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // ✅ Attendance (Admin view: vendorId + year/month)
  const now = new Date();
  const [attendanceYear, setAttendanceYear] = useState(now.getFullYear());
  const [attendanceMonth, setAttendanceMonth] = useState(now.getMonth() + 1); // 1..12
  const [attendanceSummary, setAttendanceSummary] = useState({ presentDays: 0 });

  // keep full month list + paginated view (frontend pagination)
  const [attendanceAll, setAttendanceAll] = useState([]);
  const [attendanceHistory, setAttendanceHistory] = useState([]);
  const [attendancePagination, setAttendancePagination] = useState({
    page: 1,
    limit: 10,
    totalRecords: 0,
    totalPages: 1,
    hasMore: false,
  });

  // Pagination states for job history
  const [jobPagination, setJobPagination] = useState({
    page: 1,
    limit: 10,
    totalRecords: 0,
    totalPages: 1,
    hasMore: false,
  });

  const [loadingTabs, setLoadingTabs] = useState({
    jobHistory: false,
    walletHistory: false,
    attendance: false,
    earningConfig: false,
  });

  // ✅ NEW: EARNING CONFIG states
  const [globalEarningConfig, setGlobalEarningConfig] = useState(null);
  const [effectiveEarningConfig, setEffectiveEarningConfig] = useState(null); // vendor or global (fallback)
  const [hasVendorOverride, setHasVendorOverride] = useState(false);

  // which config admin is editing
  const [earningConfigMode, setEarningConfigMode] = useState("vendor"); // "vendor" | "global"

  // form state
  const [earningForm, setEarningForm] = useState({
    baseAmount: "",
    perKmRate: "",
    thresholdDistance: "",
    bonusForFiveStar: "",
    isActive: true,
  });

  const breadcrumbItems = [
    { label: "Dashboard", path: "/", icon: "Home" },
    { label: "Vendor Management", path: "/vendor-performance-hub", icon: "Truck" },
    { label: "Vendor Details", path: `/vendors/${id}`, icon: "User", current: true },
  ];

  useEffect(() => {
    if (!vendor) fetchVendorDetails();

    if (activeTab === "job-history") fetchJobHistory(jobPagination.page);
    if (activeTab === "wallet") fetchWalletHistory();
    if (activeTab === "attendance") fetchAttendanceHistory(); // ✅ monthly admin

    // ✅ NEW: load configs on Earning Config tab
    if (activeTab === "earning-config") fetchEarningConfigs();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, activeTab]);

  const paginateAttendance = (list, page = 1, limit = 10) => {
    const total = list.length;
    const totalPages = Math.max(1, Math.ceil(total / limit));
    const safePage = Math.min(Math.max(1, page), totalPages);
    const start = (safePage - 1) * limit;
    const end = start + limit;
    const slice = list.slice(start, end);

    setAttendanceHistory(slice);
    setAttendancePagination({
      page: safePage,
      limit,
      totalRecords: total,
      totalPages,
      hasMore: safePage < totalPages,
    });
  };

  // ✅ ADMIN monthly attendance API (vendorId in URL, year/month in query)
  const fetchAttendanceHistory = async () => {
    try {
      setLoadingTabs((prev) => ({ ...prev, attendance: true }));

      const response = await axios.get(
        `${API_BASE_URL}/api/vendor-attendance/${id}/attendance/monthly`,
        {
          params: { year: attendanceYear, month: attendanceMonth },
        }
      );

      const data = response.data;

      if (data?.success) {
        const list = Array.isArray(data.presentDaysList) ? data.presentDaysList : [];
        setAttendanceSummary({ presentDays: data.presentDays || list.length });
        setAttendanceAll(list);
        paginateAttendance(list, 1, attendancePagination.limit);
      } else {
        setAttendanceSummary({ presentDays: 0 });
        setAttendanceAll([]);
        paginateAttendance([], 1, attendancePagination.limit);
        toast.error(data?.message || "Failed to load attendance");
      }
    } catch (error) {
      console.error("Error fetching attendance history:", error);
      toast.error(error?.response?.data?.message || "Failed to load attendance history");
      setAttendanceSummary({ presentDays: 0 });
      setAttendanceAll([]);
      paginateAttendance([], 1, attendancePagination.limit);
    } finally {
      setLoadingTabs((prev) => ({ ...prev, attendance: false }));
    }
  };

  const fetchVendorDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/vendors/${id}`);
      setVendor(response.data);
    } catch (error) {
      console.error("Error fetching vendor details:", error);
      toast.error("Failed to load vendor details");
    } finally {
      setLoading(false);
    }
  };

  const fetchJobHistory = async (page = 1) => {
    try {
      setLoadingTabs((prev) => ({ ...prev, jobHistory: true }));

      const params = { page, limit: jobPagination.limit };

      const response = await axios.get(
        `${API_BASE_URL}/api/orders/vendor/${id}/orders/histroy`,
        { params }
      );

      const data = response.data;

      if (data.pagination && Array.isArray(data.history)) {
        setJobHistory(data.history);
        setJobPagination({
          page: data.pagination.page || page,
          limit: data.pagination.limit || jobPagination.limit,
          totalRecords: data.pagination.totalRecords || data.history.length,
          totalPages: data.pagination.totalPages || 1,
          hasMore: data.pagination.hasMore || false,
        });
      } else if (Array.isArray(data.orders)) {
        setJobHistory(data.orders);
        setJobPagination((prev) => ({
          ...prev,
          page,
          totalRecords: data.total || data.orders.length,
          totalPages: Math.ceil((data.total || data.orders.length) / jobPagination.limit),
          hasMore:
            data.hasMore ||
            page * jobPagination.limit < (data.total || data.orders.length),
        }));
      } else if (Array.isArray(data)) {
        const startIndex = (page - 1) * jobPagination.limit;
        const endIndex = startIndex + jobPagination.limit;
        const paginatedData = data.slice(startIndex, endIndex);

        setJobHistory(paginatedData);
        setJobPagination({
          page,
          limit: jobPagination.limit,
          totalRecords: data.length,
          totalPages: Math.ceil(data.length / jobPagination.limit),
          hasMore: endIndex < data.length,
        });
      } else {
        setJobHistory([]);
        toast.error("Invalid job history response format");
      }
    } catch (error) {
      console.error("Error fetching job history:", error);
      toast.error("Failed to load job history");
      setJobHistory([]);
    } finally {
      setLoadingTabs((prev) => ({ ...prev, jobHistory: false }));
    }
  };

  const fetchWalletHistory = async () => {
    try {
      setLoadingTabs((prev) => ({ ...prev, walletHistory: true }));
      const response = await axios.get(`${API_BASE_URL}/api/vendors/earnings-history/${id}`);

      if (response.data && response.data.history) setWalletHistory(response.data.history);
      else if (Array.isArray(response.data)) setWalletHistory(response.data);
      else setWalletHistory([]);
    } catch (error) {
      console.error("Error fetching wallet history:", error);
      toast.error("Failed to load wallet history");
      setWalletHistory([]);
    } finally {
      setLoadingTabs((prev) => ({ ...prev, walletHistory: false }));
    }
  };

  const handleWalletSubmit = async (e) => {
    e.preventDefault();

    const amount = parseFloat(walletAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    if (walletType === "deduct" && amount > vendor.earnings) {
      toast.error("Deduction amount exceeds current balance");
      return;
    }

    try {
      const transactionData = {
        vendorId: Number(id),
        amount: amount,
        type: walletType === "add" ? "Credit" : "Debit",
        title: walletType === "add" ? "Wallet Top-up" : "Wallet Deduction",
        desc: walletDescription || "Manual transaction",
      };

      const response = await axios.post(`${API_BASE_URL}/api/vendors/earnings`, transactionData);

      if (response.data.transaction) {
        setVendor((prev) => ({ ...prev, earnings: response.data.newBalance }));
        fetchWalletHistory();

        setShowWalletModal(false);
        setWalletAmount("");
        setWalletDescription("");

        toast.success(`Amount ${walletType === "add" ? "added" : "deducted"} successfully!`);
      } else {
        toast.error(response.data.error || "Transaction failed");
      }
    } catch (error) {
      console.error("Error processing transaction:", error);
      toast.error(error.response?.data?.error || "Failed to process wallet transaction");
    }
  };

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
        return "bg-green-50 text-green-700 border border-green-200";
      case "in_progress":
      case "in-progress":
      case "progress":
        return "bg-blue-50 text-blue-700 border border-blue-200";
      case "pending":
        return "bg-yellow-50 text-yellow-700 border border-yellow-200";
      case "cancelled":
      case "canceled":
        return "bg-red-50 text-red-700 border border-red-200";
      default:
        return "bg-gray-50 text-gray-700 border border-gray-200";
    }
  };

  const getTransactionTypeBadge = (type) => {
    switch ((type || "").toLowerCase()) {
      case "add":
      case "credit":
        return "bg-green-50 text-green-700 border border-green-200";
      case "deduct":
      case "debit":
        return "bg-red-50 text-red-700 border border-red-200";
      case "order_earning":
      case "order-earning":
        return "bg-blue-50 text-blue-700 border border-blue-200";
      default:
        return "bg-gray-50 text-gray-700 border border-gray-200";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return { date: "N/A", time: "" };
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return { date: "N/A", time: "" };

      return {
        date: date.toLocaleDateString("en-IN"),
        time: date.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }),
      };
    } catch {
      return { date: "N/A", time: "" };
    }
  };

  const formatCurrency = (amount) => {
    const numAmount = parseFloat(amount) || 0;
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numAmount);
  };

  const handleSidebarToggle = () => setSidebarOpen(!sidebarOpen);
  const handleSidebarClose = () => setSidebarOpen(false);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === "job-history") setJobPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= jobPagination.totalPages) {
      setJobPagination((prev) => ({ ...prev, page: newPage }));
      fetchJobHistory(newPage);
    }
  };

  const walletStats = {
    totalCredit: walletHistory
      .filter((t) => ["add", "credit", "order_earning"].includes((t.type || "").toLowerCase()))
      .reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0),
    totalDebit: walletHistory
      .filter((t) => ["deduct", "debit"].includes((t.type || "").toLowerCase()))
      .reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0),
  };

  // =========================
  // ✅ NEW: EARNING CONFIG LOGIC
  // =========================
  const numOrZero = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const normalizeConfigToForm = (cfg) => ({
    baseAmount: String(cfg?.baseAmount ?? 0),
    perKmRate: String(cfg?.perKmRate ?? 0),
    thresholdDistance: String(cfg?.thresholdDistance ?? 0),
    bonusForFiveStar: String(cfg?.bonusForFiveStar ?? 0),
    isActive: cfg?.isActive !== false,
  });

  const fetchEarningConfigs = async () => {
    try {
      setLoadingTabs((p) => ({ ...p, earningConfig: true }));

      // fetch global + effective(vendor or fallback)
      const [globalRes, effectiveRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/vendor-earning-config`),
        axios.get(`${API_BASE_URL}/api/vendor-earning-config/vendor/${vendorIdNum}`),
      ]);

      const globalCfg = globalRes.data?.data || globalRes.data;
      const effCfg = effectiveRes.data?.data || effectiveRes.data;

      setGlobalEarningConfig(globalCfg);
      setEffectiveEarningConfig(effCfg);

      const isOverride = Number(effCfg?.vendorId) === vendorIdNum;
      setHasVendorOverride(isOverride);

      // default edit mode: vendor
      setEarningConfigMode("vendor");

      // prefill form with vendor override if override exists else with global (because vendor tab will create override)
      const prefill = isOverride ? effCfg : globalCfg;
      setEarningForm(normalizeConfigToForm(prefill));
    } catch (err) {
      console.error("Fetch earning configs error:", err);
      toast.error(err?.response?.data?.error || "Failed to load earning config");
      setGlobalEarningConfig(null);
      setEffectiveEarningConfig(null);
      setHasVendorOverride(false);
    } finally {
      setLoadingTabs((p) => ({ ...p, earningConfig: false }));
    }
  };

  const handleEarningModeChange = (mode) => {
    setEarningConfigMode(mode);

    if (mode === "global") {
      setEarningForm(normalizeConfigToForm(globalEarningConfig));
    } else {
      // vendor mode:
      // if override exists -> load effective (override)
      // else -> start from global values (so admin can create override quickly)
      const base = hasVendorOverride ? effectiveEarningConfig : globalEarningConfig;
      setEarningForm(normalizeConfigToForm(base));
    }
  };

  const saveEarningConfig = async () => {
    try {
      setLoadingTabs((p) => ({ ...p, earningConfig: true }));

      const payload = {
        baseAmount: numOrZero(earningForm.baseAmount),
        perKmRate: numOrZero(earningForm.perKmRate),
        thresholdDistance: numOrZero(earningForm.thresholdDistance),
        bonusForFiveStar: numOrZero(earningForm.bonusForFiveStar),
        isActive: !!earningForm.isActive,
      };

      if (earningConfigMode === "global") {
        await axios.put(`${API_BASE_URL}/api/vendor-earning-config`, payload);
        toast.success("Global earning config updated");
      } else {
        await axios.put(`${API_BASE_URL}/api/vendor-earning-config/vendor/${vendorIdNum}`, payload);
        toast.success("Vendor earning override saved");
      }

      // refresh
      await fetchEarningConfigs();
    } catch (err) {
      console.error("Save earning config error:", err);
      toast.error(err?.response?.data?.error || "Failed to save earning config");
    } finally {
      setLoadingTabs((p) => ({ ...p, earningConfig: false }));
    }
  };

  const disableVendorOverride = async () => {
    try {
      if (!hasVendorOverride) return;

      setLoadingTabs((p) => ({ ...p, earningConfig: true }));
      await axios.delete(`${API_BASE_URL}/api/vendor-earning-config/vendor/${vendorIdNum}`);
      toast.success("Vendor override disabled (fallback to global)");
      await fetchEarningConfigs();
    } catch (err) {
      console.error("Disable vendor override error:", err);
      toast.error(err?.response?.data?.error || "Failed to disable vendor override");
    } finally {
      setLoadingTabs((p) => ({ ...p, earningConfig: false }));
    }
  };

  // =========================

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!vendor) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Icon name="UserX" size={32} className="text-gray-400 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Vendor Not Found</h2>
          <Button
            onClick={() => navigate("/vendor-performance-hub")}
            className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-4 py-2"
          >
            Back to Vendors
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header onMenuToggle={handleSidebarToggle} isMenuOpen={sidebarOpen} />
   <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        isCollapsed={sidebarCollapsed}
        onCollapseToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      <main className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"}`}>
        <div className="p-4">
          <Breadcrumb items={breadcrumbItems} />

          {/* Header */}
          <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Icon name="User" size={20} className="text-blue-600" />
                </div>
                <div>
                  <h1 className="text-lg font-semibold text-gray-900">{vendor.name}</h1>
                  <p className="text-xs text-gray-600 mt-0.5">
                    #{vendor.vendorNumber || vendor.number} • {vendor.email}
                  </p>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        vendor.isOnline
                          ? "bg-green-50 text-green-700 border border-green-200"
                          : "bg-gray-50 text-gray-700 border border-gray-200"
                      }`}
                    >
                      {vendor.isOnline ? "Online" : "Offline"}
                    </span>
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        vendor.block
                          ? "bg-red-50 text-red-700 border border-red-200"
                          : "bg-green-50 text-green-700 border border-green-200"
                      }`}
                    >
                      {vendor.block ? "Blocked" : "Active"}
                    </span>
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                      {vendor.status || "Active"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-3 sm:mt-0 text-center sm:text-right">
                <div className="text-xl font-bold text-gray-900">{formatCurrency(vendor.earnings || 0)}</div>
                <p className="text-xs text-gray-600 mt-0.5">Total Earnings</p>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-lg border border-gray-200 mb-4">
            <div className="border-b border-gray-200">
              <nav className="flex -mb-px overflow-x-auto">
                {["details", "job-history", "wallet", "attendance", "earning-config"].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => handleTabChange(tab)}
                    className={`py-3 px-4 text-xs font-medium border-b-2 transition-colors duration-200 whitespace-nowrap ${
                      activeTab === tab
                        ? "border-blue-500 text-blue-600"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                  >
                    {tab === "details" && "Vendor Details"}
                    {tab === "job-history" && "Job History"}
                    {tab === "wallet" && "Wallet Dashboard"}
                    {tab === "attendance" && "Attendance"}
                    {tab === "earning-config" && "Earning Config"}
                  </button>
                ))}
              </nav>
            </div>

            <div className="p-4">
             
              {/* DETAILS TAB (unchanged UI) */}
              {activeTab === "details" && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Personal Information</h3>
                    <div className="space-y-2">
                      {[
                        { label: "Full Name", value: vendor.name },
                        { label: "Vendor Number", value: `#${vendor.vendorNumber || vendor.number}` },
                        { label: "Email", value: vendor.email || "N/A" },
                        { label: "Gender", value: vendor.gender || "N/A" },
                        { label: "Date of Birth", value: vendor.dob ? formatDate(vendor.dob).date : "N/A" },
                        { label: "Age", value: vendor.age || "N/A" },
                      ].map((item, index) => (
                        <div key={index}>
                          <label className="text-xs font-semibold text-gray-800">{item.label}</label>
                          <p className="text-xs text-gray-700 mt-0.5">{item.value}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Location Information</h3>
                    <div className="space-y-2">
                      {[
                        { label: "Address", value: vendor.address || "N/A" },
                        { label: "City", value: vendor.city || "N/A" },
                        { label: "Pincode", value: vendor.pincode || "N/A" },
                        { label: "Service Radius", value: vendor.radius ? `${vendor.radius} km` : "N/A" },
                      ].map((item, index) => (
                        <div key={index}>
                          <label className="text-xs font-semibold text-gray-800">{item.label}</label>
                          <p className="text-xs text-gray-900 mt-0.5">{item.value}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Account Information</h3>
                    <div className="space-y-2">
                      {[
                        { label: "Account Status", value: vendor.status || "Active" },
                        { label: "Block Status", value: vendor.block ? "Blocked" : "Active" },
                        { label: "Online Status", value: vendor.isOnline ? "Online" : "Offline" },
                        { label: "Total Earnings", value: formatCurrency(vendor.earnings || 0) },
                        { label: "Member Since", value: formatDate(vendor.createdAt).date },
                      ].map((item, index) => (
                        <div key={index}>
                          <label className="text-xs font-semibold text-gray-800">{item.label}</label>
                          <p className="text-xs text-gray-900 mt-0.5">{item.value}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* JOB HISTORY TAB (your existing code) */}
              {activeTab === "job-history" && (
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-sm font-semibold text-gray-900">Job History</h3>
                    <div className="flex items-center space-x-4">
                      <span className="text-xs text-gray-600">
                        Showing {(jobPagination.page - 1) * jobPagination.limit + 1} to{" "}
                        {Math.min(jobPagination.page * jobPagination.limit, jobPagination.totalRecords)} of{" "}
                        {jobPagination.totalRecords} jobs
                      </span>
                    </div>
                  </div>

                  {loadingTabs.jobHistory ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2"></div>
                      <p className="text-xs text-gray-500">Loading job history...</p>
                    </div>
                  ) : jobHistory.length === 0 ? (
                    <div className="text-center py-6">
                      <Icon name="FileText" size={32} className="text-gray-400 mx-auto mb-2" />
                      <p className="text-xs text-gray-500">No job history found</p>
                    </div>
                  ) : (
                    <>
                      <div className="overflow-x-auto mb-4">
                        <table className="w-full text-xs">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                Order ID
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                Amount
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                Status
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                Date
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {jobHistory.map((job, index) => (
                              <tr key={job.id || index} className="hover:bg-gray-50">
                                <td className="px-3 py-2 text-gray-900">#{job.orderId || job.id || index + 1}</td>
                                <td className="px-3 py-2 text-gray-900 font-medium">
                                  {formatCurrency(job.finalAmount || job.amount || 0)}
                                </td>
                                <td className="px-3 py-2">
                                  <span
                                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(
                                      job.status
                                    )}`}
                                  >
                                    {(job.status || "").replace(/_/g, " ")}
                                  </span>
                                </td>
                                <td className="px-3 py-2 text-gray-500 text-xs">
                                  {formatDate(job.date || job.createdAt).date}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {jobPagination.totalPages > 1 && (
                        <div className="flex items-center justify-between border-t border-gray-200 pt-4">
                          <div className="text-xs text-gray-600">
                            Page {jobPagination.page} of {jobPagination.totalPages}
                          </div>
                          <div className="flex items-center space-x-2">
                            <Button
                              onClick={() => handlePageChange(jobPagination.page - 1)}
                              disabled={jobPagination.page === 1}
                              variant="outline"
                              size="sm"
                              className="text-xs px-3 py-1"
                            >
                              <Icon name="ChevronLeft" size={12} className="mr-1" />
                              Previous
                            </Button>

                            <div className="flex space-x-1">
                              {Array.from({ length: Math.min(5, jobPagination.totalPages) }, (_, i) => {
                                let pageNum;
                                if (jobPagination.totalPages <= 5) pageNum = i + 1;
                                else if (jobPagination.page <= 3) pageNum = i + 1;
                                else if (jobPagination.page >= jobPagination.totalPages - 2)
                                  pageNum = jobPagination.totalPages - 4 + i;
                                else pageNum = jobPagination.page - 2 + i;

                                return (
                                  <Button
                                    key={pageNum}
                                    onClick={() => handlePageChange(pageNum)}
                                    variant={jobPagination.page === pageNum ? "solid" : "outline"}
                                    size="sm"
                                    className={`text-xs w-8 h-8 flex items-center justify-center ${
                                      jobPagination.page === pageNum ? "bg-blue-600 text-white" : "border-gray-300"
                                    }`}
                                  >
                                    {pageNum}
                                  </Button>
                                );
                              })}
                            </div>

                            <Button
                              onClick={() => handlePageChange(jobPagination.page + 1)}
                              disabled={jobPagination.page === jobPagination.totalPages || !jobPagination.hasMore}
                              variant="outline"
                              size="sm"
                              className="text-xs px-3 py-1"
                            >
                              Next
                              <Icon name="ChevronRight" size={12} className="ml-1" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* WALLET TAB (your existing code) */}
              {activeTab === "wallet" && (
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-sm font-semibold text-gray-900">Wallet Dashboard</h3>
                    <Button
                      onClick={() => setShowWalletModal(true)}
                      className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-2"
                    >
                      <Icon name="Plus" size={14} className="mr-1" />
                      Manage Wallet
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                    <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                      <div className="flex items-center">
                        <Icon name="TrendingUp" size={16} className="text-green-600 mr-2" />
                        <div>
                          <p className="text-xs font-medium text-green-800">Current Balance</p>
                          <p className="text-sm font-bold text-green-900">{formatCurrency(vendor.earnings || 0)}</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                      <div className="flex items-center">
                        <Icon name="DollarSign" size={16} className="text-blue-600 mr-2" />
                        <div>
                          <p className="text-xs font-medium text-blue-800">Total Credit</p>
                          <p className="text-sm font-bold text-blue-900">{formatCurrency(walletStats.totalCredit)}</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                      <div className="flex items-center">
                        <Icon name="TrendingDown" size={16} className="text-red-600 mr-2" />
                        <div>
                          <p className="text-xs font-medium text-red-800">Total Debit</p>
                          <p className="text-sm font-bold text-red-900">{formatCurrency(walletStats.totalDebit)}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Transaction History</h4>

                  {loadingTabs.walletHistory ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2"></div>
                      <p className="text-xs text-gray-500">Loading transaction history...</p>
                    </div>
                  ) : walletHistory.length === 0 ? (
                    <div className="text-center py-6">
                      <Icon name="CreditCard" size={32} className="text-gray-400 mx-auto mb-2" />
                      <p className="text-xs text-gray-500">No transaction history found</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                              Date
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                              Type
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                              Amount
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                              Description
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                              Balance
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {walletHistory.map((transaction, index) => {
                            const isCredit = ["add", "credit", "order_earning"].includes(
                              (transaction.type || "").toLowerCase()
                            );
                            return (
                              <tr key={transaction.id || index} className="hover:bg-gray-50">
                                <td className="px-3 py-2 text-gray-500 text-xs">
                                  {formatDate(transaction.createdAt).date}
                                </td>
                                <td className="px-3 py-2">
                                  <span
                                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getTransactionTypeBadge(
                                      transaction.type
                                    )}`}
                                  >
                                    {(transaction.type || "").replace(/_/g, " ")}
                                  </span>
                                </td>
                                <td className={`px-3 py-2 font-medium text-xs ${isCredit ? "text-green-600" : "text-red-600"}`}>
                                  {isCredit ? "+" : "-"}
                                  {formatCurrency(transaction.amount || 0)}
                                </td>
                                <td className="px-3 py-2 text-gray-900 text-xs">
                                  {transaction.desc || transaction.title || "Transaction"}
                                </td>
                                <td className="px-3 py-2 text-gray-900 text-xs">
                                  {formatCurrency(transaction.balanceAfter || transaction.amount || 0)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* ATTENDANCE TAB (your existing code) */}
              {activeTab === "attendance" && (
                <div>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                    <h3 className="text-sm font-semibold text-gray-900">Attendance</h3>

                    <div className="flex flex-wrap items-end gap-2">
                      <div>
                        <label className="block text-[11px] text-gray-600">Year</label>
                        <input
                          type="number"
                          value={attendanceYear}
                          onChange={(e) => setAttendanceYear(Number(e.target.value))}
                          className="px-2 py-1.5 border border-gray-300 rounded text-xs w-24"
                          min="2020"
                          max="2100"
                        />
                      </div>

                      <div>
                        <label className="block text-[11px] text-gray-600">Month</label>
                        <select
                          value={attendanceMonth}
                          onChange={(e) => setAttendanceMonth(Number(e.target.value))}
                          className="px-2 py-1.5 border border-gray-300 rounded text-xs"
                        >
                          {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                            <option key={m} value={m}>
                              {m}
                            </option>
                          ))}
                        </select>
                      </div>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={fetchAttendanceHistory}
                        className="text-xs px-3 py-2"
                      >
                        <Icon name="Search" size={14} className="mr-1" />
                        Apply
                      </Button>
                    </div>
                  </div>

                  <div className="mb-3 text-xs text-gray-700">
                    Present Days: <span className="font-semibold">{attendanceSummary.presentDays}</span>
                  </div>

                  {loadingTabs.attendance ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2"></div>
                      <p className="text-xs text-gray-500">Loading attendance...</p>
                    </div>
                  ) : attendanceHistory.length === 0 ? (
                    <div className="text-center py-6">
                      <Icon name="CalendarX2" size={32} className="text-gray-400 mx-auto mb-2" />
                      <p className="text-xs text-gray-500">No attendance found</p>
                    </div>
                  ) : (
                    <>
                      <div className="overflow-x-auto mb-4">
                        <table className="w-full text-xs">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                Date
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                Status
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                Check In
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                Selfie
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                Location
                              </th>
                            </tr>
                          </thead>

                          <tbody className="bg-white divide-y divide-gray-200">
                            {attendanceHistory.map((a, idx) => {
                              const dayText = a.dayIST || (a.day ? formatDate(a.day).date : "—");
                              const checkInText = a.checkInAtIST || (a.checkInAt ? formatDate(a.checkInAt).time : "—");
                              const status = a.status || "PRESENT";

                              return (
                                <tr key={a.id || idx} className="hover:bg-gray-50">
                                  <td className="px-3 py-2 text-gray-700">{dayText}</td>

                                  <td className="px-3 py-2">
                                    <span
                                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getAttendanceBadge(
                                        status
                                      )}`}
                                    >
                                      {(String(status) || "").replace(/_/g, " ")}
                                    </span>
                                  </td>

                                  <td className="px-3 py-2 text-gray-700">{checkInText}</td>

                                  <td className="px-3 py-2 text-gray-700">
                                    {a.selfieUrl ? (
                                      <a
                                        href={a.selfieUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-blue-600 underline"
                                      >
                                        View
                                      </a>
                                    ) : (
                                      "—"
                                    )}
                                  </td>

                                  <td className="px-3 py-2 text-gray-700">
                                    {a.lat && a.lng ? `${a.lat}, ${a.lng}` : "—"}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {attendancePagination.totalPages > 1 && (
                        <div className="flex items-center justify-between border-t border-gray-200 pt-4">
                          <div className="text-xs text-gray-600">
                            Page {attendancePagination.page} of {attendancePagination.totalPages}
                          </div>

                          <div className="flex items-center space-x-2">
                            <Button
                              onClick={() => paginateAttendance(attendanceAll, attendancePagination.page - 1, attendancePagination.limit)}
                              disabled={attendancePagination.page === 1}
                              variant="outline"
                              size="sm"
                              className="text-xs px-3 py-1"
                            >
                              <Icon name="ChevronLeft" size={12} className="mr-1" />
                              Previous
                            </Button>

                            <Button
                              onClick={() => paginateAttendance(attendanceAll, attendancePagination.page + 1, attendancePagination.limit)}
                              disabled={!attendancePagination.hasMore}
                              variant="outline"
                              size="sm"
                              className="text-xs px-3 py-1"
                            >
                              Next
                              <Icon name="ChevronRight" size={12} className="ml-1" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/*EARNING CONFIG TAB */}
              {activeTab === "earning-config" && (
                <div>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                    <h3 className="text-sm font-semibold text-gray-900">Earning Config</h3>

                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={fetchEarningConfigs}
                        className="text-xs px-3 py-2"
                        disabled={loadingTabs.earningConfig}
                      >
                        <Icon name="RefreshCcw" size={14} className="mr-1" />
                        Refresh
                      </Button>

                      {hasVendorOverride && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={disableVendorOverride}
                          className="text-xs px-3 py-2 border-red-200 text-red-600"
                          disabled={loadingTabs.earningConfig}
                        >
                          <Icon name="XCircle" size={14} className="mr-1" />
                          Disable Vendor Override
                        </Button>
                      )}
                    </div>
                  </div>

                  {loadingTabs.earningConfig ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2"></div>
                      <p className="text-xs text-gray-500">Loading earning config...</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {/* Effective config summary */}
                      <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="text-sm font-semibold text-gray-900">Effective Config</h4>
                            <p className="text-xs text-gray-600 mt-1">
                              This is what will be used while calculating vendor earnings.
                            </p>
                          </div>
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              hasVendorOverride
                                ? "bg-blue-50 text-blue-700 border border-blue-200"
                                : "bg-gray-50 text-gray-700 border border-gray-200"
                            }`}
                          >
                            {hasVendorOverride ? "Vendor Override" : "Global Default"}
                          </span>
                        </div>

                        <div className="mt-3 grid grid-cols-2 gap-3">
                          <div>
                            <p className="text-[11px] text-gray-600">Base Amount / Order</p>
                            <p className="text-sm font-semibold text-gray-900">
                              {formatCurrency(effectiveEarningConfig?.baseAmount ?? 0)}
                            </p>
                          </div>
                          <div>
                            <p className="text-[11px] text-gray-600">Per KM Rate</p>
                            <p className="text-sm font-semibold text-gray-900">
                              {formatCurrency(effectiveEarningConfig?.perKmRate ?? 0)}
                            </p>
                          </div>
                          <div>
                            <p className="text-[11px] text-gray-600">Threshold Distance (KM)</p>
                            <p className="text-sm font-semibold text-gray-900">
                              {Number(effectiveEarningConfig?.thresholdDistance ?? 0)}
                            </p>
                          </div>
                          <div>
                            <p className="text-[11px] text-gray-600">Bonus For 5 Star</p>
                            <p className="text-sm font-semibold text-gray-900">
                              {formatCurrency(effectiveEarningConfig?.bonusForFiveStar ?? 0)}
                            </p>
                          </div>
                        </div>

                        <div className="mt-3 text-xs text-gray-600">
                          Example:
                          <span className="ml-1 text-gray-800">
                            earning = baseAmount + max(0, (distanceKm - thresholdDistance)) * perKmRate + (5★ ? bonus : 0)
                          </span>
                        </div>
                      </div>

                      {/* Edit panel */}
                      <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <h4 className="text-sm font-semibold text-gray-900">Edit Config</h4>
                            <p className="text-xs text-gray-600 mt-1">
                              Choose whether you want to update global defaults or set a vendor override.
                            </p>
                          </div>
                        </div>

                        {/* Mode toggle */}
                        <div className="mt-3 flex items-center gap-3">
                          <label className="flex items-center gap-2 text-xs text-gray-700">
                            <input
                              type="radio"
                              name="earningMode"
                              checked={earningConfigMode === "vendor"}
                              onChange={() => handleEarningModeChange("vendor")}
                            />
                            Vendor Override
                          </label>

                          <label className="flex items-center gap-2 text-xs text-gray-700">
                            <input
                              type="radio"
                              name="earningMode"
                              checked={earningConfigMode === "global"}
                              onChange={() => handleEarningModeChange("global")}
                            />
                            Global Default
                          </label>
                        </div>

                        {earningConfigMode === "vendor" && !hasVendorOverride && (
                          <div className="mt-2 text-xs text-yellow-800 bg-yellow-50 border border-yellow-200 rounded p-2">
                            No vendor override exists. Saving will create an override for this vendor.
                          </div>
                        )}

                        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-[11px] text-gray-600 mb-1">Base Amount / Order (₹)</label>
                            <input
                              type="number"
                              value={earningForm.baseAmount}
                              onChange={(e) => setEarningForm((p) => ({ ...p, baseAmount: e.target.value }))}
                              className="w-full px-2 py-2 border border-gray-300 rounded text-xs"
                              min="0"
                              step="1"
                            />
                          </div>

                          <div>
                            <label className="block text-[11px] text-gray-600 mb-1">Per KM Rate (₹)</label>
                            <input
                              type="number"
                              value={earningForm.perKmRate}
                              onChange={(e) => setEarningForm((p) => ({ ...p, perKmRate: e.target.value }))}
                              className="w-full px-2 py-2 border border-gray-300 rounded text-xs"
                              min="0"
                              step="0.5"
                            />
                          </div>

                          <div>
                            <label className="block text-[11px] text-gray-600 mb-1">Threshold Distance (KM)</label>
                            <input
                              type="number"
                              value={earningForm.thresholdDistance}
                              onChange={(e) => setEarningForm((p) => ({ ...p, thresholdDistance: e.target.value }))}
                              className="w-full px-2 py-2 border border-gray-300 rounded text-xs"
                              min="0"
                              step="1"
                            />
                          </div>

                          <div>
                            <label className="block text-[11px] text-gray-600 mb-1">Bonus For 5 Star (₹)</label>
                            <input
                              type="number"
                              value={earningForm.bonusForFiveStar}
                              onChange={(e) => setEarningForm((p) => ({ ...p, bonusForFiveStar: e.target.value }))}
                              className="w-full px-2 py-2 border border-gray-300 rounded text-xs"
                              min="0"
                              step="1"
                            />
                          </div>
                        </div>

                        <div className="mt-3 flex items-center gap-2">
                          <input
                            id="earningIsActive"
                            type="checkbox"
                            checked={!!earningForm.isActive}
                            onChange={(e) => setEarningForm((p) => ({ ...p, isActive: e.target.checked }))}
                          />
                          <label htmlFor="earningIsActive" className="text-xs text-gray-700">
                            Active
                          </label>
                        </div>

                        <div className="mt-4 flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEarningModeChange(earningConfigMode)}
                            className="text-xs px-3 py-2"
                            disabled={loadingTabs.earningConfig}
                          >
                            Reset
                          </Button>

                          <Button
                            variant="solid"
                            size="sm"
                            onClick={saveEarningConfig}
                            className="text-xs px-3 py-2 bg-blue-600 text-white hover:bg-blue-700"
                            disabled={loadingTabs.earningConfig}
                          >
                            <Icon name="Save" size={14} className="mr-1" />
                            Save
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              
            </div>
          </div>
        </div>
      </main>

      {/* Wallet Modal */}
      {showWalletModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="px-4 py-3 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900">Manage Wallet</h3>
            </div>

            <form onSubmit={handleWalletSubmit}>
              <div className="px-4 py-3 space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Transaction Type</label>
                  <div className="flex space-x-3">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="walletType"
                        value="add"
                        checked={walletType === "add"}
                        onChange={(e) => setWalletType(e.target.value)}
                        className="text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-1.5 text-xs text-gray-700">Add Money</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="walletType"
                        value="deduct"
                        checked={walletType === "deduct"}
                        onChange={(e) => setWalletType(e.target.value)}
                        className="text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-1.5 text-xs text-gray-700">Deduct Money</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Amount (₹)</label>
                  <input
                    type="number"
                    value={walletAmount}
                    onChange={(e) => setWalletAmount(e.target.value)}
                    placeholder="Enter amount"
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    required
                    min="1"
                    step="1"
                  />
                  {walletType === "deduct" && (
                    <p className="text-xs text-gray-500 mt-1">Available balance: {formatCurrency(vendor.earnings || 0)}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Description <span className="text-gray-400">(optional)</span>
                  </label>
                  <textarea
                    value={walletDescription}
                    onChange={(e) => setWalletDescription(e.target.value)}
                    placeholder="Enter transaction description"
                    rows="2"
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex justify-end space-x-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowWalletModal(false);
                    setWalletAmount("");
                    setWalletDescription("");
                  }}
                  className="text-xs px-3 py-1.5"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="solid"
                  size="sm"
                  className={`text-xs px-3 py-1.5 ${
                    walletType === "add" ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"
                  } text-white`}
                >
                  {walletType === "add" ? "Add Money" : "Deduct Money"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
      
    </div>
  );
};

export default VendorDetails;
